import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import PropTypes from 'prop-types';
import { useAtom } from 'jotai';
// styles
import { styled } from '@mui/material/styles';
// components
import {
  Box,
  AppBar,
  Toolbar,
  Button,
  Grid,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Logo, Avatar } from '../../../../shared/components/base';
import { headerConfig, getSideConfig } from '../navbar/NavConfig';
import { NavSectionHorizontal } from '../../../../shared/components/nav-section';
import HelpModal from './components/HelpModal';
// hooks
import { useOffSetTop } from '../../../../shared/hooks';
import useHorizontalNavBar from '../navbar/hooks/useHorizontalNavBar';
import useProviderNav from '../navbar/hooks/useProviderNav';
// utils
import cssStyles from '../../../../utils/cssStyles';
// config
import { HEADER, NAVBAR } from '../../../config';
// paths
import { PATH_AUTH } from '../../../../shared/routes/paths';
// state
import { userRecordAtom } from '../../../../shared/state/atoms';
// services
import { useAuthService } from '../../../../shared/services';

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset',
})(({ isCollapse, isOffset, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
  borderBottom: '1px solid rgba(128, 128, 128, 0.5)',
  height: HEADER.main,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...{
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    },
  },
}));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  isCollapse: PropTypes.bool,
};

export default function DashboardHeader({ isCollapse = false }) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT);
  const navigate = useNavigate();
  const [user] = useAtom(userRecordAtom);
  const { signOut } = useAuthService();
  const { hasUnreadMessages, handleHelpRequest } = useHorizontalNavBar();
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const handleLogout = async () => {
    try {
      await signOut();
      navigate(PATH_AUTH.landing, { replace: true });
    } catch (error) {
      console.error(error);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navItems = headerConfig(hasUnreadMessages).flatMap((group) => group.items);
  const { providerNavItems, hasExpiredBookings } = useProviderNav();
  const sideConfig = getSideConfig(providerNavItems, hasExpiredBookings);
  const customerItems = sideConfig[0]?.items[0]?.children || [];
  const providerItems = sideConfig[0]?.items[1]?.children || [];

  const renderMobileMenu = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={toggleMobileMenu}
      PaperProps={{
        sx: { width: 280 },
      }}
    >
      <Box sx={{ p: 2 }}>
        <List>
          {/* Main Navigation Items */}
          {navItems.map((item) => {
            if ((user && item.loggedIn) || (!user && item.loggedOut)) {
              return (
                <ListItem key={item.title} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (item.path.startsWith('http')) {
                        window.location.href = item.path;
                      } else {
                        navigate(item.path);
                      }
                      toggleMobileMenu();
                    }}
                  >
                    <ListItemText
                      primary={item.title.charAt(0).toUpperCase() + item.title.slice(1)}
                      sx={{
                        '& .MuiTypography-root': {
                          fontWeight: 500,
                        },
                      }}
                    />
                    {item.title === 'messages' && item.hasUnreadMessages && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: 'red',
                          ml: 1,
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            }
            return null;
          })}

          {/* Divider */}
          <Box sx={{ my: 1.5, borderBottom: '1px solid', borderColor: 'divider' }} />

          {/* Customer Section */}
          {user && (
            <>
              <ListItem sx={{ pb: 0 }}>
                <Typography variant="subtitle1" color="primary.main" fontWeight="bold">
                  Customer
                </Typography>
              </ListItem>
              {customerItems.map((item) => (
                <ListItem key={item.title} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (item.path.startsWith('http')) {
                        window.location.href = item.path;
                      } else {
                        navigate(item.path);
                      }
                      toggleMobileMenu();
                    }}
                    sx={{ pl: 3, position: 'relative' }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {item.title}
                          {item.hasExpiredBookings && (
                            <Box
                              component="span"
                              sx={{
                                ml: 0.5,
                                mb: 1,
                                width: 10,
                                height: 10,
                                bgcolor: 'error.main',
                                borderRadius: '50%',
                                display: 'inline-block',
                              }}
                            />
                          )}
                        </Box>
                      }
                      sx={{
                        '& .MuiTypography-root': {
                          fontWeight: 400,
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}

              {/* Provider Section */}
              <ListItem sx={{ pb: 0, pt: 1 }}>
                <Typography variant="subtitle1" color="primary.main" fontWeight="bold">
                  Provider
                </Typography>
              </ListItem>
              {providerItems.map((item) => (
                <ListItem key={item.title} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (item.path.startsWith('http')) {
                        window.location.href = item.path;
                      } else {
                        navigate(item.path);
                      }
                      toggleMobileMenu();
                    }}
                    sx={{ pl: 3 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      sx={{
                        '& .MuiTypography-root': {
                          fontWeight: 400,
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          )}

          {!user ? (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    navigate(PATH_AUTH.signUp);
                    toggleMobileMenu();
                  }}
                >
                  <ListItemText
                    primary="Join Now"
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: 600,
                        color: (theme) => theme.palette.primary.main,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    navigate(PATH_AUTH.login);
                    toggleMobileMenu();
                  }}
                >
                  <ListItemText
                    primary="Login"
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: 500,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setOpenHelpModal(true);
                    toggleMobileMenu();
                  }}
                >
                  <ListItemText
                    primary="Help"
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: 500,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleLogout();
                    toggleMobileMenu();
                  }}
                >
                  <ListItemText
                    primary="Logout"
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: 500,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Box>
    </Drawer>
  );

  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset}>
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { xs: 2, lg: 5 },
        }}
      >
        <Grid container alignItems="center">
          <Grid item xs>
            <Logo />
          </Grid>
          <Grid item>
            {isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {user && (
                  <Avatar
                    alt="User"
                    src={user.details.profilePictureUrl || user.avatar}
                    sx={{ width: 36, height: 36, mr: 1 }}
                  />
                )}
                <IconButton onClick={toggleMobileMenu} sx={{ color: 'text.primary' }} aria-label="menu">
                  <MenuIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center' }}>
                <NavSectionHorizontal navConfig={headerConfig(hasUnreadMessages)} user={user} />
                {!user ? (
                  <>
                    <Button
                      variant="containedPrimary"
                      sx={{
                        ml: 2,
                        borderWidth: 2,
                        borderRadius: '15px',
                      }}
                      onClick={() => navigate(PATH_AUTH.signUp)}
                    >
                      Join Now
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{
                        ml: 2,
                        borderWidth: 2,
                        borderRadius: '15px',
                      }}
                      onClick={() => navigate(PATH_AUTH.login)}
                    >
                      Login
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="text"
                      onClick={() => {
                        setOpenHelpModal(true);
                      }}
                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                    >
                      Help
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        handleLogout();
                      }}
                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                    >
                      Logout
                    </Button>
                    <Avatar
                      alt="User"
                      src={user.details.profilePictureUrl || user.avatar}
                      sx={{ width: 40, height: 40, ml: 2 }}
                    />
                  </>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Toolbar>
      {renderMobileMenu()}
      <HelpModal open={openHelpModal} onClose={() => setOpenHelpModal(false)} handleHelpRequest={handleHelpRequest} />
    </RootStyle>
  );
}
